import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useGetFacilityPhotosQuery } from "Api/Queries/Merchants/useGetFacilityPhotosQuery";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { toImageDataUrl } from "Components/Shared/Merchant/Facilities/utils";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import type { MerchantFacilityDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.Facilities;

type Props = {
  merchantPublicID: string;
  facility: Partial<MerchantFacilityDto>;
};

type Photo = { label: string; url: string };

/**
 * Read-only preview of the stored facility photos (exterior + interior) for the facility
 * detail. The list already says which slots are filled, so the photos are fetched only
 * when there is something to show.
 */
export const FacilityPhotos = ({ merchantPublicID, facility }: Props) => {
  const { t } = useResource();
  const hasStoredPhotos =
    !!facility.hasPhotoOutside || !!facility.hasPhotoInside;
  const { data, isLoading, isError } = useGetFacilityPhotosQuery(
    merchantPublicID,
    facility.publicID,
    hasStoredPhotos,
  );

  const isPhotosLoading = hasStoredPhotos && isLoading;
  const hasPhotosFailed = hasStoredPhotos && isError;

  const photos = [
    {
      label: t(RESOURCES.Form.Fields.PhotoOutside),
      url: toImageDataUrl(data?.photoOutside, data?.photoOutsideExtension),
    },
    {
      label: t(RESOURCES.Form.Fields.PhotoInside),
      url: toImageDataUrl(data?.photoInside, data?.photoInsideExtension),
    },
  ].filter((photo): photo is Photo => !!photo.url);

  return (
    <ValuesBox title={t(RESOURCES.Form.Legend.Photos)}>
      {!hasStoredPhotos && <Typography>{t(RESOURCES.NoPhotos)}</Typography>}
      {isPhotosLoading && <Skeleton height={200} variant="rounded" />}
      {hasPhotosFailed && (
        <ErrorText>{t(RESOURCES.PhotosLoadingError)}</ErrorText>
      )}
      {photos.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={3}>
          {photos.map(({ label, url }) => (
            <Stack key={label} gap={1} flex="1 1 240px" minWidth={0}>
              <Typography variant="subtitle2">{label}</Typography>
              <Box
                component="img"
                src={url}
                alt={label}
                width="100%"
                maxHeight={320}
                borderRadius={1}
                sx={{ objectFit: "contain", objectPosition: "left top" }}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </ValuesBox>
  );
};
